export type TargetOs = "ios" | "android";
export type DevicePlatform = "ios" | "android" | "desktop";

// iPadOS 13+ Safari reports a desktop "Macintosh" UA (no "ipad" token) but is a
// touch device, so detect that case explicitly in addition to the UA tokens.
function isIOSDevice(ua: string): boolean {
  if (/iphone|ipad|ipod/.test(ua)) {
    return true;
  }
  return typeof navigator !== "undefined" && navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
}

/**
 * Best-effort device platform from the user agent.
 * iPhone/iPad/iPod (incl. iPadOS-as-Mac) → "ios"; Android → "android"; everything
 * else → "desktop". SSR-safe: returns "desktop" when there is no `navigator`.
 */
export function detectPlatform(): DevicePlatform {
  if (typeof navigator === "undefined") {
    return "desktop";
  }
  const ua = navigator.userAgent.toLowerCase();
  if (isIOSDevice(ua)) {
    return "ios";
  }
  if (/android/.test(ua)) {
    return "android";
  }
  return "desktop";
}

/**
 * One of Meta's in-app browsers: Facebook and Messenger add `FBAN`/`FBAV`/`FB_IAB` to the user agent,
 * Instagram adds `Instagram`, and Threads adds `Barcelona`. SSR-safe: false when there is no `navigator`.
 */
export function isMetaInAppBrowser(ua: string = typeof navigator === "undefined" ? "" : navigator.userAgent): boolean {
  return /FBAN|FBAV|FB_IAB|Instagram|Barcelona/.test(ua);
}

/**
 * Target app store for the current device: iPhone/iPad → App Store ("ios");
 * Android and everything else (incl. desktop/unknown) → Google Play ("android").
 */
export function detectTargetOs(): TargetOs {
  return detectPlatform() === "ios" ? "ios" : "android";
}
